/**
 * Ball-heat and team-shape pitch panels (Ben, 2026-09-21) — shared by the
 * match page's team-split view and the dashboard builder's cards, so the
 * two surfaces cannot drift. Narrow props on purpose: a cache, a live
 * playhead ref, an fps — no widget context.
 *
 * Coordinates are tracking-space: centimetres, centre-origin (KNOWLEDGE.md
 * §tracking); the SVG mapping matches LivePlayersPanel's.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react'

import { sidesAtFrame } from './teamSplit'
import type { TrackingDataCache } from './trackingLoader'

/** The playhead as VideoPlayer last reported it (~4x/s), with when. The
 *  panels that want EVERY frame interpolate from this between reports and
 *  freeze when reports stop (pause). Lives here (not TeamSplitPanels) so
 *  the shape/ball panels can glide without an import cycle; TeamSplitPanels
 *  re-exports it for its long-standing importers. */
export interface LiveFrameRef {
  current: { frame: number; at: number }
}

export function useLiveFrame(frameRef: LiveFrameRef, fps: number): number {
  const [frame, setFrame] = useState(0)
  useEffect(() => {
    let raf = 0
    const loop = () => {
      const s = frameRef.current
      // Reports arrive ~every 250ms while playing; older than 700ms means
      // paused — hold the last reported frame instead of running ahead.
      const age = performance.now() - s.at
      setFrame(age > 700 ? s.frame
        : Math.floor(s.frame + (age / 1000) * fps))
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [frameRef, fps])
  return frame
}

/** Pitch SVG logical size, the LivePlayersPanel convention. */
const PS = 6
export const PITCH_W = 105 * PS
export const PITCH_H = 68 * PS
const HALF_LEN_CM = 105 * 100 / 2
const HALF_WID_CM = 68 * 100 / 2
const ppx = (x: number) => ((x + HALF_LEN_CM) / (2 * HALF_LEN_CM)) * PITCH_W
const ppy = (y: number) => ((HALF_WID_CM - y) / (2 * HALF_WID_CM)) * PITCH_H

export function PitchLines(): ReactElement {
  const stroke = 'rgba(255,255,255,0.35)'
  return (
    <>
      <rect x={0} y={0} width={PITCH_W} height={PITCH_H}
            fill="rgba(0,0,0,0.25)" stroke={stroke} />
      <line x1={PITCH_W / 2} x2={PITCH_W / 2} y1={0} y2={PITCH_H} stroke={stroke} />
      <circle cx={PITCH_W / 2} cy={PITCH_H / 2} r={9.15 * PS} fill="none" stroke={stroke} />
      <rect x={0} y={PITCH_H / 2 - 20.16 * PS} width={16.5 * PS} height={40.32 * PS}
            fill="none" stroke={stroke} />
      <rect x={PITCH_W - 16.5 * PS} y={PITCH_H / 2 - 20.16 * PS} width={16.5 * PS}
            height={40.32 * PS} fill="none" stroke={stroke} />
    </>
  )
}

// ── field utilization (zone occupancy) ─────────────────────────────────

/** Thirds along the pitch × channels across it — the grid coaches talk
 *  in (left / half-space / centre / half-space / right). */
export const ZONES_X = 3
export const ZONES_Y = 5

/**
 * Fraction of sampled time spent in each zone, [row][col] with row 0 the
 * top channel and col 0 the left third (screen space, like every mini
 * pitch here). Fractions sum to 1 when there are samples; all-zero when
 * not. cm, centre-origin. Exported for tests.
 */
export function zoneOccupancy(
  points: { x: number; y: number }[],
): number[][] {
  const grid: number[][] = Array.from({ length: ZONES_Y },
    () => Array<number>(ZONES_X).fill(0))
  if (!points.length) return grid
  const clamp = (v: number, hi: number) => Math.min(hi, Math.max(0, v))
  for (const p of points) {
    const col = clamp(Math.floor(((p.x + HALF_LEN_CM) / (2 * HALF_LEN_CM)) * ZONES_X), ZONES_X - 1)
    const row = clamp(Math.floor(((HALF_WID_CM - p.y) / (2 * HALF_WID_CM)) * ZONES_Y), ZONES_Y - 1)
    grid[row]![col]! += 1
  }
  for (const row of grid) {
    for (let c = 0; c < row.length; c++) row[c] = row[c]! / points.length
  }
  return grid
}

/** Centre of gravity and 1σ spread of the sampled positions — one glyph
 *  saying where the window's work happened and how compact it was.
 *  Null below two samples. cm, centre-origin. Exported for tests. */
export function spreadEllipse(
  points: { x: number; y: number }[],
): { cx: number; cy: number; rx: number; ry: number } | null {
  if (points.length < 2) return null
  const n = points.length
  const cx = points.reduce((s, p) => s + p.x, 0) / n
  const cy = points.reduce((s, p) => s + p.y, 0) / n
  const rx = Math.sqrt(points.reduce((s, p) => s + (p.x - cx) ** 2, 0) / n)
  const ry = Math.sqrt(points.reduce((s, p) => s + (p.y - cy) ** 2, 0) / n)
  return { cx, cy, rx, ry }
}

/** Window choices for the utilization card. Infinity = since kickoff. */
const ZONE_WINDOWS = [
  { id: '1m', label: '1m', seconds: 60 },
  { id: '5m', label: '5m', seconds: 300 },
  { id: 'match', label: 'Match', seconds: Infinity },
] as const

/** Keep the scan inside the tracking cache's comfort zone (the heat panel
 *  measured ~120 samples as the LRU-friendly budget). */
const ZONE_MAX_SAMPLES = 120

/**
 * Field utilization: % of time per zone (thirds × channels), shaded in the
 * team's color with the percentages printed where they matter, plus the
 * window's centre-of-gravity ellipse. Replaces the raw touch-density dust
 * that a one-minute window rendered (Ben, 2026-09-21).
 */
export function ZoneOccupancyPanel({
  cache, frame, team, color, kickoffFrame, fps, homeStartDirection,
  homeIndex,
}: {
  cache: TrackingDataCache | null
  /** The 1 Hz tick — an aggregate view has no need of the rAF playhead. */
  frame: number
  team: 0 | 1
  color: string
  /** First frame of actual play; the Match window starts here, and no
   *  window reaches before it — warmup is not utilization. */
  kickoffFrame: number
  fps: number
  /** Orients the keeper exclusion. Null keeps the keeper in the count. */
  homeStartDirection: string | null
  /** The tracking index of the home side (lib/homeIndex). */
  homeIndex: 0 | 1
}): ReactElement {
  const [windowId, setWindowId] = useState<typeof ZONE_WINDOWS[number]['id']>('1m')

  const phase = cache?.getFrameData(frame)?.phase ?? 1
  const ownSign = ownGoalSign(homeStartDirection, phase, team, homeIndex)

  // The keeper is an identity that survives corners — see pickKeeper.
  const keeperJerseyRef = useRef<number | null>(null)
  const currentTeam = (cache?.getFrameData(frame)?.players ?? [])
    .filter((p) => p.team === team)
  const currentKeeper = ownSign
    ? pickKeeper(currentTeam, ownSign, keeperJerseyRef.current) : null
  if (currentKeeper) keeperJerseyRef.current = currentKeeper.jersey
  const keeperJersey = currentKeeper?.jersey ?? null

  const points = useMemo(() => {
    if (!cache) return []
    const win = ZONE_WINDOWS.find((w) => w.id === windowId)!
    const step = Math.max(1, Math.round(fps))
    const from = Math.max(kickoffFrame,
      win.seconds === Infinity ? kickoffFrame : frame - win.seconds * step)
    const span = Math.max(1, frame - from)
    const stride = Math.max(step, Math.ceil(span / ZONE_MAX_SAMPLES))
    const out: { x: number; y: number }[] = []
    for (let f = from; f <= frame; f += stride) {
      const d = cache.getFrameData(f)
      if (!d) continue
      const samplePhase = d.phase ?? 1
      const teamPs = d.players.filter((p) => p.team === team)
      // Outfield only, when the end is known — the keeper's static minutes
      // otherwise inflate the deepest zone's share. The frame's own phase
      // orients the exclusion; the sample is then ROTATED into the current
      // phase's frame so a straddling window (and Match especially) reads
      // as one match under one orientation, not half a match mislabelled.
      const sampleSign = ownGoalSign(homeStartDirection, samplePhase, team, homeIndex)
      const k = sampleSign ? pickKeeper(teamPs, sampleSign, keeperJersey) : null
      for (const p of teamPs) {
        if (p !== k) out.push(orientToPhase(p, samplePhase, phase))
      }
    }
    return out
  }, [cache, frame, team, kickoffFrame, fps, windowId, phase,
      homeStartDirection, homeIndex, keeperJersey])

  const grid = useMemo(() => zoneOccupancy(points), [points])
  const ellipse = useMemo(() => spreadEllipse(points), [points])
  const maxPct = Math.max(...grid.flat(), 0.0001)
  const cellW = PITCH_W / ZONES_X
  const cellH = PITCH_H / ZONES_Y

  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${PITCH_W} ${PITCH_H}`} role="img"
           aria-label="Share of outfield time spent in each zone, oriented to the current half">
        {grid.map((row, r) => row.map((pct, c) => (
          <rect key={`${r}-${c}`} x={c * cellW} y={r * cellH}
                width={cellW} height={cellH} fill={color}
                opacity={0.06 + 0.6 * (pct / maxPct)} />
        )))}
        <PitchLines />
        {ellipse && (
          <g>
            <ellipse cx={ppx(ellipse.cx)} cy={ppy(ellipse.cy)}
                     rx={(ellipse.rx / (2 * HALF_LEN_CM)) * PITCH_W}
                     ry={(ellipse.ry / (2 * HALF_WID_CM)) * PITCH_H}
                     fill="none" stroke="rgba(255,255,255,0.75)"
                     strokeDasharray="5,4" strokeWidth={1.5} />
            <circle cx={ppx(ellipse.cx)} cy={ppy(ellipse.cy)} r={3.5}
                    fill="rgba(255,255,255,0.9)" />
          </g>
        )}
        {grid.map((row, r) => row.map((pct, c) => (
          pct >= 0.1 ? (
            <text key={`t${r}-${c}`} x={c * cellW + cellW / 2}
                  y={r * cellH + cellH / 2 + 4} textAnchor="middle"
                  className="gms-team-panel__jersey" fill="#fff">
              {Math.round(pct * 100)}%
            </text>
          ) : null
        )))}
        {!points.length && (
          <text x={PITCH_W / 2} y={PITCH_H / 2} textAnchor="middle"
                className="gms-team-panel__jersey">
            {cache ? 'no samples in this window yet' : 'loading tracking\u2026'}
          </text>
        )}
      </svg>
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        {ZONE_WINDOWS.map((w) => (
          <button key={w.id} type="button"
                  onClick={() => setWindowId(w.id)}
                  style={{
                    font: 'inherit', fontSize: 11, cursor: 'pointer',
                    padding: '1px 8px', borderRadius: 999,
                    background: 'none', color: 'inherit',
                    border: `1px solid ${windowId === w.id ? color : 'rgba(255,255,255,0.25)'}`,
                    opacity: windowId === w.id ? 1 : 0.7,
                  }}>
            {w.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── ball heat ──────────────────────────────────────────────────────────

/** Ball heat window: one sample per second over the last two minutes —
 *  the same per-tick cache budget the team heat panel stays inside. */
export const BALL_SPAN_SECONDS = 120

/** The comet: the last seconds of ball travel as a connected path fading
 *  into the live dot, because 1 Hz heat blobs can never say which way the
 *  ball went — the ball outruns the sampling and the story reads as
 *  disconnected puffs (Ben, 2026-09-21). */
const TRAIL_SECONDS = 15
/** Trail samples per second — fine enough to draw passes as lines. */
const TRAIL_HZ = 4
/** A bigger jump than this between quarter-second samples is not ball
 *  flight, it is a cut, a throw-in retake or lost tracking (fastest real
 *  ball flight ≈ 9 m per quarter second). */
const TRAIL_MAX_JUMP_CM = 1200

/**
 * Split a time-ordered path wherever consecutive points jump farther than
 * a ball can travel — drawing across those gaps would paint phantom passes
 * from one restart to the next. Exported for tests.
 */
export function trailSegments<P extends { x: number; y: number }>(
  points: P[], maxJumpCm: number = TRAIL_MAX_JUMP_CM,
): P[][] {
  const segments: P[][] = []
  let current: P[] = []
  for (const p of points) {
    const prev = current[current.length - 1]
    if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) > maxJumpCm) {
      if (current.length > 1) segments.push(current)
      current = []
    }
    current.push(p)
  }
  if (current.length > 1) segments.push(current)
  return segments
}

export function BallHeatPanel({ cache, frameRef, fps }: {
  cache: TrackingDataCache | null
  frameRef: LiveFrameRef
  fps: number
}): ReactElement {
  const step = Math.max(1, Math.round(fps))
  const live = useLiveFrame(frameRef, fps)
  // The two-minute scan re-runs once per second; the live ball dot below
  // moves every animation frame — pitch control's cost split.
  const frame = Math.floor(live / step) * step
  const livePhase = cache?.getFrameData(frame)?.phase ?? 1
  const blurId = useId()
  const points = useMemo(() => {
    if (!cache) return []
    const out: { x: number; y: number; age: number }[] = []
    const from = Math.max(0, frame - BALL_SPAN_SECONDS * step)
    for (let f = from; f <= frame; f += step) {
      const d = cache.getFrameData(f)
      // This half only: the ball has no orientation to rotate, but a
      // window that reaches across the break narrates a different
      // game-state as if it were this one (SDET #374 r2, low).
      if (!d?.ball || (d.phase ?? 1) !== livePhase) continue
      out.push({ x: d.ball.x, y: d.ball.y, age: (frame - f) / step })
    }
    return out
  }, [cache, frame, step, livePhase])

  // The comet trail: finer-grained recent path, re-read once a second from
  // frames that are warm in the cache (they sit at the playhead).
  const trail = useMemo(() => {
    if (!cache) return []
    const stride = Math.max(1, Math.round(step / TRAIL_HZ))
    const out: { x: number; y: number; age: number }[] = []
    for (let f = Math.max(0, frame - TRAIL_SECONDS * step); f <= frame; f += stride) {
      const d = cache.getFrameData(f)
      if (!d?.ball || (d.phase ?? 1) !== livePhase) continue
      out.push({ x: d.ball.x, y: d.ball.y, age: (frame - f) / step })
    }
    return trailSegments(out)
  }, [cache, frame, step, livePhase])

  if (!cache) {
    return <p className="dw-empty">Tracking still loading…</p>
  }
  const latest = cache.getFrameData(live)?.ball ?? points[points.length - 1] ?? null
  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${PITCH_W} ${PITCH_H}`} role="img"
           aria-label="Where the ball has been over the last two minutes">
        <defs>
          <filter id={blurId}><feGaussianBlur stdDeviation={7} /></filter>
        </defs>
        <PitchLines />
        <g filter={`url(#${blurId})`}>
          {points.map((p, i) => {
            // Recency carries the meaning: recent history is hot and large,
            // two minutes ago is a faint ember.
            const recency = 1 - p.age / BALL_SPAN_SECONDS
            return (
              <circle key={i} cx={ppx(p.x)} cy={ppy(p.y)}
                      r={9 + 6 * recency} fill="#F4B942"
                      opacity={0.03 + 0.3 * recency ** 2} />
            )
          })}
        </g>
        {/* The comet: the last seconds as a connected path, brightening
            into the live dot; broken where the ball did not travel (out of
            play, cuts) so no phantom pass is drawn across the pitch. */}
        {trail.map((seg, s) => (
          <g key={s}>
            {seg.slice(1).map((p, i) => {
              const prev = seg[i]!
              const recency = 1 - p.age / TRAIL_SECONDS
              return (
                <line key={i} x1={ppx(prev.x)} y1={ppy(prev.y)}
                      x2={ppx(p.x)} y2={ppy(p.y)}
                      stroke="#fff" strokeLinecap="round"
                      strokeWidth={1 + 2 * recency}
                      opacity={0.1 + 0.7 * recency} />
              )
            })}
          </g>
        ))}
        {latest && (
          <circle cx={ppx(latest.x)} cy={ppy(latest.y)} r={5}
                  fill="#fff" stroke="rgba(0,0,0,0.5)" />
        )}
        {!points.length && (
          <text x={PITCH_W / 2} y={PITCH_H / 2} textAnchor="middle"
                className="gms-team-panel__jersey">no ball data in this window</text>
        )}
      </svg>
    </div>
  )
}

// ── team shape ─────────────────────────────────────────────────────────

/** Convex hull (Andrew monotone chain), points in drawing order. Exported
 *  for the panel tests — the shape outline must contain every player. */
export function convexHull(
  pts: { x: number; y: number }[],
): { x: number; y: number }[] {
  if (pts.length <= 3) return [...pts]
  const p = [...pts].sort((a, b) => a.x - b.x || a.y - b.y)
  const cross = (o: { x: number; y: number }, a: { x: number; y: number },
                 b: { x: number; y: number }) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  const lower: typeof p = []
  for (const pt of p) {
    while (lower.length >= 2
        && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, pt) <= 0) lower.pop()
    lower.push(pt)
  }
  const upper: typeof p = []
  for (const pt of [...p].reverse()) {
    while (upper.length >= 2
        && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, pt) <= 0) upper.pop()
    upper.push(pt)
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)]
}

/**
 * Which side of the screen this team's OWN GOAL is on at this phase:
 * -1 = negative x (left), +1 = positive x (right). Derived from the
 * recorded start direction through sidesAtFrame — the one chokepoint that
 * already answers who defends which end — never from player geometry: the
 * first cut inferred the end from the most-extreme |x| player, and every
 * ordinary attack with a striker deeper than the keeper flipped the whole
 * card (SDET review, PR #374). Null when the direction was never recorded
 * — the oriented readings BLOCK rather than guess (ledger 26, RULED B).
 * Exported for tests.
 */
export function ownGoalSign(
  homeStartDirection: string | null | undefined,
  phase: number,
  team: 0 | 1,
  homeIndex: 0 | 1,
): 1 | -1 | null {
  const sides = sidesAtFrame(homeStartDirection, phase, homeIndex)
  if (!sides) return null
  // The "left team" is the one whose goal is on the left.
  return sides.left === team ? -1 : 1
}

/**
 * A sampled point, expressed in the CURRENT phase's frame of reference.
 * Ends swap at halftime, and the swap IS a 180° rotation of the pitch —
 * so rotating other-phase samples lets a window span both halves under
 * one orientation, which is what a "Match" control promises (the first
 * cut silently filtered Match down to half a match; SDET #374, round 2).
 * Exported for tests.
 */
export function orientToPhase<P extends { x: number; y: number }>(
  p: P, samplePhase: number, currentPhase: number,
): { x: number; y: number } {
  return samplePhase === currentPhase ? { x: p.x, y: p.y } : { x: -p.x, y: -p.y }
}

/**
 * The keeper: the player deepest toward their OWN goal — which requires
 * knowing which end that is. Null when the frame is too thin to say.
 * Exported for tests.
 */
export function keeperOf<P extends { x: number }>(
  players: P[], ownSign: 1 | -1,
): P | null {
  if (players.length < 3) return null
  return players.reduce((a, b) => (b.x * ownSign > a.x * ownSign ? b : a))
}

/**
 * The keeper as an IDENTITY, not a position: depth bootstraps the pick,
 * then the jersey sticks while it is on the pitch. Position alone flipped
 * to the deepest DEFENDER whenever the keeper came up for a corner,
 * excluding the wrong player from the hull and jumping the printed line
 * ~40 m at the exact moment worth watching (SDET #374, round 2). A
 * substitution removes the jersey from the frame and depth re-bootstraps.
 * Exported for tests.
 */
export function pickKeeper<P extends { x: number; jersey: number }>(
  players: P[], ownSign: 1 | -1, prevJersey: number | null,
): P | null {
  if (prevJersey != null) {
    const kept = players.find((p) => p.jersey === prevJersey)
    if (kept) return kept
  }
  return keeperOf(players, ownSign)
}

/**
 * The last defensive line — the offside line the opposition plays against.
 * The law counts the SECOND-last defender, so the line is the
 * next-deepest teammate after the keeper, toward the team's own goal.
 * x in cm, centre-origin. Exported for tests.
 */
export function lastDefensiveLine(
  players: { x: number }[], ownSign: 1 | -1,
): { x: number; ownSign: 1 | -1 } | null {
  const keeper = keeperOf(players, ownSign)
  if (!keeper) return null
  const rest = players.filter((p) => p !== keeper)
  const line = rest.reduce((a, b) => (b.x * ownSign > a.x * ownSign ? b : a))
  return { x: line.x, ownSign }
}

/** Height of the line above the team's own goal line, in metres. */
export function lineHeightMeters(line: { x: number; ownSign: 1 | -1 }): number {
  return (HALF_LEN_CM - line.x * line.ownSign) / 100
}

/** Faded snapshots behind the current shape: seconds back, oldest first. */
const SHAPE_TRAIL_SECONDS = [60, 45, 30, 15] as const
const SHAPE_TRAIL_ALPHA = [0.10, 0.16, 0.24, 0.34] as const

/** Hull outline as SVG polygon points. */
const hullPts = (ps: { x: number; y: number }[], flipX = false) =>
  convexHull(ps)
    .map((p) => `${(flipX ? PITCH_W - ppx(p.x) : ppx(p.x)).toFixed(1)},`
      + ppy(p.y).toFixed(1)).join(' ')

/** Whether a view drawn in true orientation must mirror for `team` to
 *  attack `wantRight` on screen at this phase: its own goal's side
 *  (ownGoalSign) says which way it attacks in the data. False when that is
 *  unknown — nothing is mirrored on a guess. Exported for tests. */
export function mirrorFor(
  homeStartDirection: string | null | undefined, phase: number,
  team: 0 | 1, wantRight: boolean, homeIndex: 0 | 1,
): boolean {
  const own = ownGoalSign(homeStartDirection, phase, team, homeIndex)
  if (own == null) return false
  const dataRight = own < 0          // own goal on the left: attacks right
  return dataRight !== wantRight
}

export function TeamShapePanel({
  cache, frameRef, fps, team, color, oppColor, teamLabel, homeStartDirection,
  homeIndex, flipX: flipXProp = false, attackRight,
}: {
  cache: TrackingDataCache | null
  frameRef: LiveFrameRef
  fps: number
  /** The tracking team index; homeIndex says which is home. */
  team: 0 | 1
  color: string
  /** The OPPONENT's color — their offside line draws in it. */
  oppColor: string
  teamLabel: string
  /** The recorded first-half attack direction of the HOME side — what
   *  orients the keeper exclusion and the offside line. Null blocks both
   *  (the un-oriented hull still draws) rather than guessing. */
  homeStartDirection: string | null
  /** The tracking index of the home side (lib/homeIndex). */
  homeIndex: 0 | 1
  /** Mirror left-right at RENDER only — the Workshop dashboard lets the
   *  two teams face opposing directions and swap, to match the video.
   *  Keeper and line logic run on the true data; jerseys stay upright. */
  flipX?: boolean
  /** The direction this team should attack on screen. The shape is drawn
   *  in true (camera) orientation, where a team's end changes by phase:
   *  the panel mirrors when its data attacks the other way at the
   *  current phase (mirrorFor), and not at all when the start direction
   *  is unknown. Takes precedence over flipX. */
  attackRight?: boolean
}): ReactElement {
  const step = Math.max(1, Math.round(fps))
  // Live layer (current shape, line, ball) glides at animation rate; the
  // faded history snapshots step at whole seconds — they are history.
  const live = useLiveFrame(frameRef, fps)
  const frame = Math.floor(live / step) * step

  const liveData = cache?.getFrameData(live) ?? null
  const phase = liveData?.phase ?? 1
  const ownSign = ownGoalSign(homeStartDirection, phase, team, homeIndex)
  // every drawn element (hulls, history, players, ball, both lines, the
  // label) mirrors by this one value; keeper and line logic stay on the
  // true data
  const flipX = attackRight === undefined ? flipXProp
    : mirrorFor(homeStartDirection, phase, team, attackRight, homeIndex)
  const fx = (x: number) => (flipX ? PITCH_W - ppx(x) : ppx(x))

  const wholeTeam = (liveData?.players ?? []).filter((p) => p.team === team)
  /** The shape is the OUTFIELD's — the keeper stands apart from it (Ben,
   *  2026-09-21) — but only when the defended end is KNOWN; and the keeper
   *  is an IDENTITY (pickKeeper), so coming up for a corner does not hand
   *  the exclusion to the deepest defender. */
  const keeperJerseyRef = useRef<number | null>(null)
  const keeper = ownSign
    ? pickKeeper(wholeTeam, ownSign, keeperJerseyRef.current) : null
  if (keeper) keeperJerseyRef.current = keeper.jersey
  const now = keeper ? wholeTeam.filter((p) => p !== keeper) : wholeTeam
  // The LINE stays positional on purpose: the law counts the second-last
  // opponent whoever they are, so second-deepest-of-everyone is correct
  // with the keeper in goal AND with the keeper upfield for a corner. Only
  // the hull needs the keeper's identity.
  const line = ownSign ? lastDefensiveLine(wholeTeam, ownSign) : null
  // The OPPONENT's offside line — the one this team's attack plays
  // against. Same law, their end: their own-goal sign comes from
  // ownGoalSign on the opposite index, not from negating ours.
  const opp: 0 | 1 = team === 0 ? 1 : 0
  const oppTeam = (liveData?.players ?? []).filter((p) => p.team === opp)
  const oppSign = ownGoalSign(homeStartDirection, phase, opp, homeIndex)
  const oppLine = oppSign ? lastDefensiveLine(oppTeam, oppSign) : null
  const ball = liveData?.ball ?? null

  // The faded history, at 1 Hz and memoized — the first cut rebuilt four
  // hulls per ANIMATION frame (SDET review, PR #374). Other-phase ghosts
  // are ROTATED into the current orientation (ends swap at halftime), so
  // the minute around the break still reads as one story.
  const keeperJersey = keeper?.jersey ?? null
  const trailShapes = useMemo(() => {
    if (!cache) return []
    return SHAPE_TRAIL_SECONDS.flatMap((back, i) => {
      const f = frame - back * step
      if (f < 0) return []
      const d = cache.getFrameData(f)
      if (!d) return []
      const samplePhase = d.phase ?? 1
      const all = d.players.filter((p) => p.team === team)
      const sampleSign = ownGoalSign(homeStartDirection, samplePhase, team, homeIndex)
      const k = sampleSign ? pickKeeper(all, sampleSign, keeperJersey) : null
      const ps = (k ? all.filter((p) => p !== k) : all)
        .map((p) => ({ ...p, ...orientToPhase(p, samplePhase, phase) }))
      if (ps.length < 3) return []
      return [{ back, alpha: SHAPE_TRAIL_ALPHA[i]!, ps,
                hull: hullPts(ps, flipX) }]
    })
  }, [cache, frame, step, team, phase, homeStartDirection, homeIndex,
      keeperJersey, flipX])

  // Memoized on the frame OBJECT: the cache hands back the same object for
  // the same frame, so a paused player rebuilds nothing — the history hulls
  // were memoised for exactly this reason (SDET #374, round 2, low).
  const nowHull = useMemo(
    () => (now.length >= 3 ? hullPts(now, flipX) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now` derives from these
    [liveData, team, keeperJersey, flipX])

  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${PITCH_W} ${PITCH_H}`} role="img"
           aria-label={`${teamLabel} shape, current over the last minute`}>
        <PitchLines />
        {trailShapes.map((t) => (
          <g key={t.back} opacity={t.alpha}>
            <polygon points={t.hull} fill={color} fillOpacity={0.25}
                     stroke={color} />
            {t.ps.map((p, j) => (
              <circle key={j} cx={fx(p.x)} cy={ppy(p.y)} r={3.5} fill={color} />
            ))}
          </g>
        ))}
        {oppLine && (
          <line x1={fx(oppLine.x)} x2={fx(oppLine.x)} y1={0} y2={PITCH_H}
                stroke={oppColor} strokeDasharray="4,3" strokeWidth={1.5}
                opacity={0.6} />
        )}
        {line && (() => {
          // The label hangs off the line on the mirrored side too.
          const towardOwn = flipX ? line.ownSign < 0 : line.ownSign > 0
          return (
            <g>
              <line x1={fx(line.x)} x2={fx(line.x)} y1={0} y2={PITCH_H}
                    stroke={color} strokeDasharray="6,4" strokeWidth={2} />
              <text x={fx(line.x) + (towardOwn ? -6 : 6)} y={14}
                    textAnchor={towardOwn ? 'end' : 'start'}
                    className="gms-team-panel__jersey" fill="#fff">
                line {lineHeightMeters(line).toFixed(0)}m
              </text>
            </g>
          )
        })()}
        {nowHull && (
          <polygon points={nowHull} fill={color} fillOpacity={0.10}
                   stroke={color} strokeWidth={1.5} />
        )}
        {now.map((p, j) => (
          <g key={j}>
            <circle cx={fx(p.x)} cy={ppy(p.y)} r={8} fill={color}
                    stroke="rgba(0,0,0,0.4)" />
            <text x={fx(p.x)} y={ppy(p.y) + 3} textAnchor="middle"
                  className="gms-team-panel__jersey">{p.jersey}</text>
          </g>
        ))}
        {ball && (
          <circle cx={fx(ball.x)} cy={ppy(ball.y)} r={4.5}
                  fill="#fff" stroke="rgba(0,0,0,0.5)" />
        )}
        {!now.length && (
          <text x={PITCH_W / 2} y={PITCH_H / 2} textAnchor="middle"
                className="gms-team-panel__jersey">loading tracking&hellip;</text>
        )}
      </svg>
    </div>
  )
}
